import axios from "axios";
import React from "react";
import { Modal } from "react-bootstrap";
import {
  Globe2,
  Laptop,
  LogOut,
  Monitor,
  RefreshCw,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { v4 } from "uuid";
import DashboardMenu from "../../components/dashboardMenu/dashboardMenu";
import { StoreContext } from "../../store";
import {
  accountSessionListRequest,
  accountSessionRevocationRequest,
} from "./accountSessionsApi";
import {
  accountSessionClientLabel,
  accountSessionErrorMessage,
  extractAccountSessions,
  formatAccountSessionTime,
  shouldEndCurrentWebSession,
} from "./accountSessionsPolicy";
import "./accountSessions.css";

const ACCOUNT_SESSIONS_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 120000;
const requestConfig = (request) => ({
  ...request,
  headers: {
    "Cache-Control": "no-store",
    Pragma: "no-cache",
    "X-Request-Id": v4(),
  },
  timeout: ACCOUNT_SESSIONS_TIMEOUT_MS,
});

const clientIcon = (clientType) => {
  const normalized = String(clientType || "").toLowerCase();
  if (["ios", "android"].includes(normalized)) return Smartphone;
  if (normalized === "windows") return Monitor;
  if (["browser", "web"].includes(normalized)) return Globe2;
  return Laptop;
};

const confirmationForAction = (kind, session) => {
  if (kind === "others") {
    return {
      kind,
      session: null,
      title: "Revoke Other Sessions",
      message: "Revoke every session except this one?",
      confirmLabel: "Revoke Others",
    };
  }
  if (kind === "all") {
    return {
      kind,
      session: null,
      title: "Revoke All Sessions",
      message: "Revoke every session, including this one?",
      confirmLabel: "Revoke All",
    };
  }
  const current = session?.current === true;
  return {
    kind: "single",
    session,
    title: current ? "Sign Out This Session" : "Revoke Session",
    message: current
      ? "Revoke this session and return to sign in?"
      : `Revoke ${session?.applicationLabel || session?.deviceLabel || "this session"}?`,
    confirmLabel: current ? "Sign Out" : "Revoke",
  };
};

const AccountSessions = () => {
  const { replace } = useHistory();
  const { setResponse } = React.useContext(StoreContext);
  const mountedRef = React.useRef(true);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyAction, setBusyAction] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [statusMessage, setStatusMessage] = React.useState("");
  const [confirmation, setConfirmation] = React.useState(null);

  const endCurrentSession = React.useCallback(() => {
    setResponse({isAuth: false});
    replace("/auth/login");
  }, [replace, setResponse]);

  const handleRequestFailure = React.useCallback((error, fallback) => {
    const responseData = error?.response?.data || {};
    if (shouldEndCurrentWebSession({responseData})) {
      endCurrentSession();
      return;
    }
    if (mountedRef.current) {
      setErrorMessage(accountSessionErrorMessage(responseData, fallback));
    }
  }, [endCurrentSession]);

  const loadSessions = React.useCallback(async ({quiet = false} = {}) => {
    if (!quiet && mountedRef.current) {
      setLoading(true);
    }
    if (mountedRef.current) {
      setErrorMessage("");
    }
    try {
      const response = await axios.request(requestConfig(accountSessionListRequest()));
      const responseData = response?.data || {};
      if (responseData.success === false) {
        const requestError = new Error("Session list request failed");
        requestError.response = response;
        throw requestError;
      }
      if (mountedRef.current) {
        setSessions(extractAccountSessions(responseData));
      }
    } catch (error) {
      handleRequestFailure(error, "Sessions could not be loaded. Please try again.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [handleRequestFailure]);

  React.useEffect(() => {
    mountedRef.current = true;
    loadSessions();
    return () => {
      mountedRef.current = false;
    };
  }, [loadSessions]);

  const closeConfirmation = () => {
    if (!busyAction) {
      setConfirmation(null);
    }
  };

  const executeRevocation = async () => {
    if (!confirmation || busyAction) {
      return;
    }

    const action = confirmation;
    const actionIncludedCurrent = action.kind === "all" || action.session?.current === true;
    const actionKey = action.kind === "single" ? action.session.sessionManagementId : action.kind;
    const request = requestConfig(accountSessionRevocationRequest({
      kind: action.kind,
      sessionManagementId: action.session?.sessionManagementId,
    }));

    setBusyAction(actionKey);
    setErrorMessage("");
    setStatusMessage("");
    try {
      const response = await axios.request(request);
      const responseData = response?.data || {};
      if (responseData.success === false) {
        const requestError = new Error("Session revocation failed");
        requestError.response = response;
        throw requestError;
      }
      if (shouldEndCurrentWebSession({responseData, actionIncludedCurrent})) {
        endCurrentSession();
        return;
      }
      if (mountedRef.current) {
        setConfirmation(null);
        setStatusMessage(action.kind === "others" ? "Other sessions revoked." : "Session revoked.");
      }
      await loadSessions({quiet: true});
    } catch (error) {
      const responseData = error?.response?.data || {};
      if (shouldEndCurrentWebSession({responseData})) {
        endCurrentSession();
        return;
      }
      if (mountedRef.current) {
        setErrorMessage(accountSessionErrorMessage(
          responseData,
          "The session could not be revoked. Please try again."
        ));
        setConfirmation(null);
      }
    } finally {
      if (mountedRef.current) {
        setBusyAction("");
      }
    }
  };

  const otherSessionCount = sessions.filter((session) => !session.current).length;

  return (
    <section className="dashboardSec dt-account-sessions-page">
      <div className="dt-account-sessions-wrap">
        <div className="container">
          <DashboardMenu />
          <div className="dt-account-sessions-heading">
            <div>
              <h2><span>Account Sessions</span></h2>
              <div className="dt-account-sessions-count" aria-live="polite">
                {sessions.length} active {sessions.length === 1 ? "session" : "sessions"}
              </div>
            </div>
            <div className="dt-account-sessions-actions">
              <button
                type="button"
                className="dt-session-button dt-session-button--secondary"
                onClick={() => setConfirmation(confirmationForAction("others"))}
                disabled={loading || Boolean(busyAction) || otherSessionCount === 0}
              >
                <ShieldCheck size={18} aria-hidden="true" />
                Revoke Others
              </button>
              <button
                type="button"
                className="dt-session-button dt-session-button--danger"
                onClick={() => setConfirmation(confirmationForAction("all"))}
                disabled={loading || Boolean(busyAction) || sessions.length === 0}
              >
                <LogOut size={18} aria-hidden="true" />
                Revoke All
              </button>
            </div>
          </div>

          <div className="dt-account-sessions-feedback" aria-live="polite" aria-atomic="true">
            {statusMessage && <div className="dt-session-notice dt-session-notice--success">{statusMessage}</div>}
            {errorMessage && (
              <div className="dt-session-notice dt-session-notice--error" role="alert">
                <span>{errorMessage}</span>
                <button type="button" onClick={() => loadSessions()} disabled={loading || Boolean(busyAction)}>
                  <RefreshCw size={16} aria-hidden="true" />
                  Retry
                </button>
              </div>
            )}
          </div>

          <div className="dt-account-sessions-list" aria-busy={loading}>
            {loading && sessions.length === 0 && (
              <div className="dt-account-sessions-loading" role="status">
                <RefreshCw size={20} aria-hidden="true" />
                Loading sessions...
              </div>
            )}
            {!loading && sessions.length === 0 && !errorMessage && (
              <div className="dt-account-sessions-empty">No active sessions.</div>
            )}
            {sessions.map((session) => {
              const ClientIcon = clientIcon(session.clientType);
              const title = session.applicationLabel || session.deviceLabel || accountSessionClientLabel(session.clientType);
              const actionBusy = busyAction === session.sessionManagementId;
              return (
                <article className="dt-account-session" key={session.sessionManagementId}>
                  <div className="dt-account-session-icon" aria-hidden="true">
                    <ClientIcon size={24} />
                  </div>
                  <div className="dt-account-session-main">
                    <div className="dt-account-session-title-row">
                      <h3>{title}</h3>
                      {session.current && <span className="dt-account-session-current">Current</span>}
                    </div>
                    <div className="dt-account-session-client">
                      {session.deviceLabel && session.deviceLabel !== title
                        ? `${session.deviceLabel} - ${accountSessionClientLabel(session.clientType)}`
                        : accountSessionClientLabel(session.clientType)}
                    </div>
                    <dl className="dt-account-session-metadata">
                      <div>
                        <dt>Created</dt>
                        <dd><time dateTime={session.createdAt || undefined}>{formatAccountSessionTime(session.createdAt)}</time></dd>
                      </div>
                      <div>
                        <dt>Last active</dt>
                        <dd><time dateTime={session.lastActiveAt || undefined}>{formatAccountSessionTime(session.lastActiveAt)}</time></dd>
                      </div>
                      <div>
                        <dt>Rolling expiration</dt>
                        <dd><time dateTime={session.expiresAt || undefined}>{formatAccountSessionTime(session.expiresAt)}</time></dd>
                      </div>
                      {session.approximateLocation && (
                        <div>
                          <dt>Approximate location</dt>
                          <dd>{session.approximateLocation}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  <div className="dt-account-session-command">
                    <button
                      type="button"
                      className={session.current
                        ? "dt-session-button dt-session-button--secondary"
                        : "dt-session-button dt-session-button--danger-outline"}
                      onClick={() => setConfirmation(confirmationForAction("single", session))}
                      disabled={Boolean(busyAction)}
                      aria-label={session.current ? "Sign out this session" : `Revoke ${title}`}
                    >
                      <LogOut size={17} aria-hidden="true" />
                      {actionBusy ? "Revoking..." : session.current ? "Sign Out" : "Revoke"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      <Modal
        show={Boolean(confirmation)}
        onHide={closeConfirmation}
        centered
        className="dt-session-confirmation"
        backdrop={busyAction ? "static" : true}
        keyboard={!busyAction}
      >
        <Modal.Header closeButton={!busyAction}>
          <Modal.Title>{confirmation?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmation?.message}</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="dt-session-button dt-session-button--secondary"
            onClick={closeConfirmation}
            disabled={Boolean(busyAction)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="dt-session-button dt-session-button--danger"
            onClick={executeRevocation}
            disabled={Boolean(busyAction)}
          >
            <LogOut size={17} aria-hidden="true" />
            {busyAction ? "Revoking..." : confirmation?.confirmLabel}
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default AccountSessions;
